import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import KeycloakService from './session';

const WARNING_THRESHOLD_MS = 5 * 60 * 1000;
const TOKEN_LOG_INTERVAL_MS = 20 * 1000;

const formatRemainingTime = (remainingMs: number): string => {
  const totalSeconds = Math.max(Math.ceil(remainingMs / 1000), 0);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

export default function SessionTimeoutManager(): JSX.Element | null {
  const [remainingMs, setRemainingMs] = useState<number>(() =>
    KeycloakService.getRemainingSessionMs()
  );
  const [expirationMessage, setExpirationMessage] = useState<string>('');
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    const unsubscribe = KeycloakService.subscribeToSessionExpiration((message) => {
      setExpirationMessage(message);
      setRemainingMs(0);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    const syncSessionState = () => {
      if (!KeycloakService.isLoggedIn()) {
        setRemainingMs(0);
        return;
      }

      const nextRemainingMs = KeycloakService.getRemainingSessionMs();
      setRemainingMs(nextRemainingMs);

      if (nextRemainingMs <= 0 && !KeycloakService.isSessionExpirationHandled()) {
        KeycloakService.handleAuthenticationFailure();
      }
    };

    syncSessionState();

    const interval = window.setInterval(syncSessionState, 1000);

    return () => {
      window.clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => {
      KeycloakService.logRemainingSessionTime();
    }, TOKEN_LOG_INTERVAL_MS);

    return () => {
      window.clearInterval(interval);
    };
  }, []);

  const showWarning = useMemo(
    () =>
      KeycloakService.isLoggedIn() &&
      expirationMessage === '' &&
      remainingMs > 0 &&
      remainingMs <= WARNING_THRESHOLD_MS,
    [expirationMessage, remainingMs]
  );

  const handleConfirmExpiration = async () => {
    setIsRedirecting(true);
    await KeycloakService.redirectToLoginAfterExpiration();
  };

  if (!KeycloakService.isLoggedIn() && expirationMessage === '') {
    return null;
  }

  return (
    <>
      {showWarning && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: (theme) => theme.zIndex.modal + 1,
            p: 2
          }}
        >
          <Alert severity="warning" variant="filled">
            Sua sessao termina em {formatRemainingTime(remainingMs)}. Salve seu trabalho.
          </Alert>
        </Box>
      )}

      <Dialog open={expirationMessage !== ''} disableEscapeKeyDown fullWidth maxWidth="xs">
        <DialogTitle>Fim da sessao</DialogTitle>
        <DialogContent>
          <DialogContentText>{expirationMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmExpiration} variant="contained" disabled={isRedirecting}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
